import React, { useEffect, useRef, useState } from 'react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  children,
  title,
}) => {
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);

  // Блокировка скролла при открытом модальном окне
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setDragY(0);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Закрытие по клику на backdrop
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Обработчики свайпа для закрытия
  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - startY.current;
    // Разрешаем только тянуть вниз (положительное значение)
    if (diff > 0) {
      setDragY(diff);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    // Если потянули больше чем на 100px - закрываем
    if (dragY > 100) {
      onClose();
    }
    setDragY(0);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black bg-opacity-50 transition-opacity"
      onClick={handleBackdropClick}
    >
      {/*
        Mobile/Tablet: BottomSheet снизу экрана (max-h-[90vh], rounded-t-3xl)
        Desktop (lg+): Полноэкранная модалка по центру (95vh высота, rounded-3xl)
      */}
      <div
        className={`
          w-full bg-white shadow-2xl
          overflow-hidden flex flex-col

          /* Mobile/Tablet: снизу экрана */
          max-w-2xl max-h-[90vh] rounded-t-3xl

          /* Desktop: полноэкранная по центру */
          lg:max-w-6xl lg:max-h-[95vh] lg:h-[95vh] lg:rounded-3xl lg:mx-4

          ${isOpen ? 'lg:scale-100' : 'lg:scale-95'}
          ${!isDragging ? 'transition-transform duration-300 ease-out' : ''}
        `}
        style={{
          animation: !isDragging && isOpen ? 'slideUp 0.3s ease-out' : undefined,
          // Применяем смещение при свайпе только на mobile/tablet
          transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
        }}
      >
        {/* Зона для свайпа - заголовок с индикатором (mobile) и кнопкой закрытия (desktop) */}
        <div
          className="flex items-center justify-between p-4 border-b border-background-300 cursor-grab active:cursor-grabbing lg:cursor-default"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Mobile/Tablet: только индикатор перетаскивания */}
          <div className="w-full flex justify-center lg:hidden">
            <div className="w-12 h-1.5 bg-background-400 rounded-full"></div>
          </div>

          {/* Desktop: заголовок слева и кнопка закрытия справа */}
          <div className="hidden lg:flex lg:w-full lg:items-center lg:justify-between">
            {title && (
              <h3 className="text-xl lg:text-2xl font-bold text-text-primary">{title}</h3>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-background-200 rounded-lg transition-colors ml-auto"
              aria-label="Закрыть"
            >
              <svg className="w-6 h-6 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Заголовок для mobile/tablet - отдельно под индикатором */}
        {title && (
          <div className="px-6 py-4 border-b border-background-300 lg:hidden">
            <h3 className="text-xl font-bold text-text-primary">{title}</h3>
          </div>
        )}

        {/* Контент */}
        <div className="flex-1 overflow-y-auto p-6 lg:p-8">
          {children}
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }

        @keyframes slideDown {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(100%);
          }
        }

        /* Desktop анимация */
        @media (min-width: 1024px) {
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          @keyframes slideDown {
            from {
              opacity: 1;
              transform: scale(1);
            }
            to {
              opacity: 0;
              transform: scale(0.95);
            }
          }
        }
      `}</style>
    </div>
  );
};
